import { defaultAllSelection } from "services/constants/selections";
import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { ArrowForwardRounded } from "@mui/icons-material";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
} from "recharts";
import { designColors, designFontFamily } from "services/constants/designTokens";
import { useFetch } from "services/hooks/useFetch";
import { ctaButtonSx, sectionCardSx, SectionHeader } from "./homeUi";

// Matches the app's existing EOD Report status palette where it overlaps
// (statusToneMap in TeamZone/EODReport/index.js): Reopened's orange is
// reused for "Pending review" since that screen has no distinct "pending"
// status of its own. Submitted/Approved intentionally use teal/blue here
// (rather than that screen's blue/green) to match the reference design for
// this card — see PR discussion if the two ever need to be reconciled.
const SUBMITTED_COLOR = "#1D67E0";
const APPROVED_COLOR = "#169A55";
const REOPENED_COLOR = "#F08A24";

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  return (
    <Mui.Box
      sx={{
        background: designColors.white,
        border: `1px solid ${designColors.slate200}`,
        borderRadius: "8px",
        px: 1.25,
        py: 0.75,
        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
      }}
    >
      <Mui.Typography sx={{ fontSize: 12.5, color: designColors.slate700 }}>
        {entry.payload.label}: <strong>{entry.value}</strong>
      </Mui.Typography>
    </Mui.Box>
  );
};

export const PendingReportsSection = ({
  navigateToModule,
  loading = false,
  todayDate = moment().format("YYYY-MM-DD"),
  refreshKey = 0,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);

  const [rangeMode, setRangeMode] = useState("today");

  const weekStartDate = useMemo(
    () => moment(todayDate, "YYYY-MM-DD").startOf("isoWeek").format("YYYY-MM-DD"),
    [todayDate],
  );
  const {
    result: weekReportsRaw,
    loading: reportsLoading,
    setLoad: reloadReports,
  } = useFetch(
    domain && userDetails?.id
      ? `${domain}eod-reports/?user_id=${userDetails.id}&start_date=${weekStartDate}&end_date=${todayDate}&department_id=${defaultAllSelection}&company_id=${requestCompanyId}`
      : null,
  );

  // useFetch already fetches on mount/url change on its own — this only
  // needs to react to Home's Refresh button (refreshKey bump), which
  // otherwise never touches this section's independent eod-reports fetch.
  // Guarded on a truthy refreshKey so it doesn't also fire a redundant
  // extra fetch on mount, when refreshKey is still its default 0.
  useEffect(() => {
    if (refreshKey) {
      reloadReports(true);
    }
  }, [refreshKey, reloadReports]);

  const weekReports = useMemo(
    () =>
      (Array.isArray(weekReportsRaw) ? weekReportsRaw : []).filter(
        (report) => report.status !== "Draft",
      ),
    [weekReportsRaw],
  );

  const buildScopeStats = (reports) => {
    const submitted = reports.filter((r) => r.status === "Submitted").length;
    const approved = reports.filter((r) => r.status === "Approved").length;
    const reopened = reports.filter((r) => r.status === "Reopened").length;
    return {
      submitted,
      approved,
      reopened,
      total: submitted + approved + reopened,
    };
  };

  const scopeStats = useMemo(() => {
    const todayReports = weekReports.filter((r) => r.report_date === todayDate);
    return {
      today: buildScopeStats(todayReports),
      week: buildScopeStats(weekReports),
    };
  }, [weekReports, todayDate]);

  const activeStats = scopeStats[rangeMode === "today" ? "today" : "week"];
  const chartData = [
    { key: "submitted", label: "Submitted", value: activeStats.submitted, color: SUBMITTED_COLOR },
    { key: "approved", label: "Approved", value: activeStats.approved, color: APPROVED_COLOR },
    { key: "reopened", label: "Reopened", value: activeStats.reopened, color: REOPENED_COLOR },
  ];

  const isLoading = loading || reportsLoading;

  return (
    <Mui.Box
      sx={{
        ...sectionCardSx,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {!isLoading && (
        <SectionHeader
          title="Daily Reports Status"
          tooltip="Submitted, approved, and reopened daily work reports for your team. Use Today or This week to change the scope."
          actionLabel="View all"
          onAction={() => navigateToModule("EOD Report")}
        />
      )}
      {isLoading ? (
        <>
          <Mui.Stack direction="row" justifyContent="space-between" sx={{ pt: 1.25 }}>
            <Mui.Stack spacing={0.5}>
              <Mui.Skeleton variant="text" width={110} height={18} />
              <Mui.Skeleton variant="text" width={140} height={16} />
            </Mui.Stack>
            <Mui.Skeleton variant="rounded" width={120} height={28} sx={{ borderRadius: 999 }} />
          </Mui.Stack>
          <Mui.Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-end"
            sx={{ flex: 1, pt: 1.5 }}
          >
            <Mui.Skeleton variant="rounded" width={48} height={40} />
            <Mui.Skeleton variant="rounded" width={48} height={80} />
            <Mui.Skeleton variant="rounded" width={48} height={110} />
          </Mui.Stack>
          <Mui.Skeleton
            variant="rounded"
            height={36}
            sx={{ borderRadius: 999, mt: 1.5 }}
          />
        </>
      ) : (
        <>
          {/* Single flex:1 wrapper around all the content, matching the
              sibling cards in this row (WorkforceStatusSection,
              PendingApprovalsSection, AttendanceExceptionsSection) — it's
              what pins the CTA button to the bottom of the card once the
              row stretches every card to the tallest sibling's height. */}
          <Mui.Stack spacing={2} sx={{ flex: 1, pt: 1.25 }}>
            <Mui.Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Mui.Stack spacing={0.15}>
                <Mui.Typography
                  sx={{ fontSize: 13.5, fontWeight: 500, color: designColors.slate900 }}
                >
                  Daily report workflow
                </Mui.Typography>
                <Mui.Typography sx={{ fontSize: 12, color: designColors.slate500 }}>
                  {activeStats.total} team reports tracked
                </Mui.Typography>
              </Mui.Stack>
              <Mui.Stack
                direction="row"
                spacing={0.25}
                sx={{
                  border: `1px solid ${designColors.slate200}`,
                  borderRadius: 999,
                  p: 0.25,
                  flexShrink: 0,
                }}
              >
                {[
                  { key: "today", label: "Today" },
                  { key: "week", label: "This week" },
                ].map((option) => (
                  <Mui.Box
                    key={option.key}
                    component="button"
                    type="button"
                    onClick={() => setRangeMode(option.key)}
                    sx={{
                      border: "none",
                      cursor: "pointer",
                      borderRadius: 999,
                      px: 1.1,
                      py: 0.35,
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: designFontFamily,
                      backgroundColor:
                        rangeMode === option.key ? designColors.tealTint : "transparent",
                      color:
                        rangeMode === option.key ? designColors.tealDark : designColors.slate500,
                    }}
                  >
                    {option.label}
                  </Mui.Box>
                ))}
              </Mui.Stack>
            </Mui.Stack>

            <ResponsiveContainer width="100%" height={140}>
              <BarChart
                data={chartData}
                margin={{ top: 24, right: 8, left: 0, bottom: 0 }}
                barCategoryGap="30%"
              >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={designColors.slate100}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tick={(props) => {
                      const { x, y, payload } = props;
                      const entry = chartData.find((d) => d.label === payload.value);
                      return (
                        <text
                          x={x}
                          y={y + 12}
                          textAnchor="middle"
                          fontSize={11}
                          fontWeight={600}
                          fontFamily={designFontFamily}
                          fill={entry?.color || designColors.slate600}
                        >
                          {payload.value}
                        </text>
                      );
                    }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                    width={24}
                    tick={{ fontSize: 11, fill: designColors.slate400, fontFamily: designFontFamily }}
                  />
                  <Tooltip cursor={false} content={<ChartTooltip />} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={44}>
                    {chartData.map((entry) => (
                      <Cell key={entry.key} fill={entry.color} />
                    ))}
                    <LabelList
                      dataKey="value"
                      position="top"
                      content={(props) => {
                        const { x, y, width, value, index } = props;
                        const entry = chartData[index];
                        return (
                          <text
                            x={x + width / 2}
                            y={y - 8}
                            textAnchor="middle"
                            fontSize={13.5}
                            fontWeight={700}
                            fontFamily={designFontFamily}
                            fill={entry?.color || "#0F172A"}
                          >
                            {value}
                          </text>
                        );
                      }}
                    />
                  </Bar>
              </BarChart>
            </ResponsiveContainer>

            <Mui.Stack direction="row" spacing={2} justifyContent="center">
              {chartData.map((entry) => (
                <Mui.Stack key={entry.key} direction="row" spacing={0.75} alignItems="center">
                  <Mui.Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      backgroundColor: entry.color,
                    }}
                  />
                  <Mui.Typography sx={{ fontSize: 12.5, color: designColors.slate700 }}>
                    {entry.label}
                  </Mui.Typography>
                  <Mui.Typography
                    sx={{ fontSize: 12.5, fontWeight: 700, color: designColors.slate900 }}
                  >
                    {entry.value}
                  </Mui.Typography>
                </Mui.Stack>
              ))}
            </Mui.Stack>
          </Mui.Stack>

          <Mui.Button
            fullWidth
            endIcon={<ArrowForwardRounded />}
            sx={{ ...ctaButtonSx, mt: 1.5 }}
            onClick={() => navigateToModule("EOD Report")}
          >
            Go to Daily Reports
          </Mui.Button>
        </>
      )}
    </Mui.Box>
  );
};
